<script setup lang="ts">
type IconName =
  | "bowling"
  | "billiard"
  | "tennis"
  | "cinema"
  | "cinema3d"
  | "pingpong"
  | "console"
  | "shooting"
  | "arcade"
  | "babysitter"
  | "parking"
  | "football"
  | "volleyball"
  | "basketball"
  | "gym"
  | "bike";

interface Activity {
  icon: IconName;
  icons?: IconName[];
  label: string;
  price: string;
  note?: string;
  free?: boolean;
}

interface ActivitiesBlock {
  header: string;
  intro?: string;
  note?: string;
  items: Activity[];
}

defineProps<{
  block: ActivitiesBlock;
}>();

const { t } = useI18n();
</script>

<template>
  <section
    class="relative overflow-hidden bg-gradient-to-b from-white via-amber-50/30 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-32 -right-16 h-80 w-80 rounded-full bg-emerald-200/25 blur-3xl dark:bg-emerald-500/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-amber-200/30 blur-3xl dark:bg-amber-500/10"
    />

    <div data-reveal class="relative mx-auto w-full max-w-7xl px-4 py-8 md:py-12">
      <header class="mb-6 md:mb-8">
        <span
          class="font-body text-xs uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400"
        >
          {{ t("services.activities.eyebrow") }}
        </span>
        <h2
          class="mt-3 max-w-5xl font-display text-3xl italic leading-tight text-slate-900 md:text-5xl dark:text-white"
        >
          {{ block.header }}
        </h2>
        <div class="mt-3 h-1 w-16 rounded-full bg-amber-400" />
        <p
          v-if="block.intro"
          class="mt-3 max-w-3xl font-body text-base leading-relaxed text-slate-600 md:mt-4 md:text-lg dark:text-slate-300"
        >
          {{ block.intro }}
        </p>
        <div
          class="mt-4 flex flex-wrap gap-x-5 gap-y-2 font-body text-xs text-slate-500 dark:text-slate-400"
        >
          <span class="inline-flex items-center gap-2">
            <span class="inline-block size-2 rounded-full bg-emerald-500" />
            {{ t("services.activities.legend.free") }}
          </span>
          <span class="inline-flex items-center gap-2">
            <span class="inline-block size-2 rounded-full bg-amber-500" />
            {{ t("services.activities.legend.paid") }}
          </span>
        </div>
      </header>

      <ul
        class="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-4"
      >
        <li
          v-for="(item, i) in block.items"
          :key="i"
          :class="[
            'group relative flex flex-col gap-3 rounded-2xl border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:bg-slate-800/60',
            item.free
              ? 'border-emerald-200/80 hover:border-emerald-400 dark:border-emerald-900/50'
              : 'border-slate-200 hover:border-amber-400 dark:border-slate-700',
          ]"
        >
          <span
            v-if="item.free"
            class="absolute right-3 top-3 inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 font-body text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
          >
            {{ t("services.activities.free_label") }}
          </span>

          <span
            v-if="item.icons && item.icons.length > 1"
            :class="[
              'inline-flex h-14 w-14 items-center justify-center rounded-2xl p-1.5 transition',
              item.free
                ? 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400'
                : 'bg-amber-50 text-amber-600 group-hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-400',
            ]"
          >
            <span class="grid h-full w-full grid-cols-2 gap-0.5">
              <ServicesServiceIcon
                v-for="(ic, idx) in item.icons.slice(0, 4)"
                :key="idx"
                :name="ic"
                class="h-full w-full"
              />
            </span>
          </span>
          <span
            v-else
            :class="[
              'inline-flex h-11 w-11 items-center justify-center rounded-full transition',
              item.free
                ? 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400'
                : 'bg-amber-50 text-amber-600 group-hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-400',
            ]"
          >
            <ServicesServiceIcon :name="item.icon" class="h-6 w-6" />
          </span>

          <span
            class="font-display text-lg font-semibold text-slate-900 dark:text-white"
          >
            {{ item.label }}
          </span>

          <span
            v-if="!item.free"
            class="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 font-body text-sm font-semibold text-amber-800 self-start tabular-nums dark:bg-amber-900/30 dark:text-amber-200"
          >
            {{ item.price }}
          </span>

          <span
            v-if="item.note"
            class="font-body text-xs text-slate-500 dark:text-slate-400"
          >
            {{ item.note }}
          </span>
        </li>
      </ul>

      <p
        v-if="block.note"
        class="mt-5 inline-flex items-start gap-2 font-body text-sm text-slate-600 md:mt-6 dark:text-slate-300"
      >
        <svg
          class="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
        <span>{{ block.note }}</span>
      </p>
    </div>
  </section>
</template>
